import { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  IonCol,
  IonGrid,
  IonRow,
  IonItem,
  IonLabel,
  IonList,
  IonIcon,
  IonText,
  IonButton,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  IonInput,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  IonSelect,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  IonSelectOption,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  IonTextarea,
  IonSpinner,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  IonModal,
  IonPopover,
  IonContent,
  IonCheckbox,
} from "@ionic/react";
import PymblePage from "./PymblePage";
import {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  add,
  addCircle,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  addOutline,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  calendarNumberOutline,
  calendarOutline,
  closeOutline,
  fileTray,
  fileTrayFullOutline,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  mail,
  mailOutline,
  person,
  personOutline,
  refresh,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  refreshCircle,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  refreshOutline,
  trashOutline,
} from "ionicons/icons";
import TimeShort from "../components/common/TimeShort";
import { displayMsg, Spinner } from "pymble-ui/components";
import { CalendarNav, CalendarNext, CalendarPrev, CalendarToday, Datepicker } from "@mobiscroll/react";
import CoverTeacherSelect, {
  SelectedCoverTeacher,
} from "../components/common/CoverTeacherSelect";
import { formatDate, formatDateTime } from "../utils";
import dateAndTime from "date-and-time";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import { useForm } from "react-hook-form";
import {
  TeacherAbsence,
  useTeacherAbsences,
} from "../hooks/useTeacherAbsences";
import { useTeacherLessons } from "../hooks/useTeacherLessons";
import { useLessonDetail, LessonDetail } from "../hooks/useLessonDetail";
import { useRoomList } from "../hooks/useRoomList";
import { PeriodDetail, TimetableType } from "../hooks/useTimetablePeriods";
import PeriodSelector from "../components/common/PeriodSelector";
import TimetableSelector from "../components/common/TimetableSelector";
import CoverWorkForm from "../components/common/CoverWorkForm";
import { OnCallTeacher } from "../hooks/useOnCallTeachers";
import ContactProfileImage from "../components/common/ContactProfileImage";
import { app } from "../config";
import SelectedContext from "../contexts/SelectedContext";
import { HtmlText } from "pymble-ui/components";
import { useCoverTeachers } from "../hooks/useCoverTeachers";
import CoverTeacherChooser from "../components/common/CoverTeacherChooser";
import AddLessonButton from "../components/common/AddLessonButton";
import ClosePopoverButton from "../components/common/ClosePopoverButton";
import { useDictionary } from "../hooks/useDictionary";

import "./Home.css";

const TIMETABLE_TYPE_DEFAULT = "Secondary";

/**
 * Determines if a lesson requires cover teachers based on the RequiresCover field
 * and fallback to Class.CoverTeachersRequired.
 * @param lesson - The lesson to check
 * @returns true if the lesson requires cover teachers, false otherwise
 */
const doesLessonRequireCover = (lesson: LessonDetail): boolean => {
  // If RequiresCover is explicitly set (true or false), use that value
  // This applies to both regular lessons and events
  if (lesson?.RequiresCover !== null && lesson?.RequiresCover !== undefined) {
    return lesson.RequiresCover;
  }

  // For Events without explicit RequiresCover setting, default to false
  if (lesson?.Type === "Event") {
    return false;
  }

  // For regular lessons, fall back to the class-level setting
  return Boolean(lesson?.Class?.CoverTeachersRequired);
};

type CoverFormInputs = {
  room: string;
  reason: string;
  cover_summary: string;
  cover_detail: string;
};

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface HomeProps { }

const Home: React.FC<HomeProps> = () => {
  const { selectedAbsenceDate, setSelectedAbsenceDate, firstDate, setFirstDate, lastDate, setLastDate } =
    useContext(SelectedContext);
  const [absenceDate, setAbsenceDate] = useState<string | null>(
    formatDate(new Date().toISOString())
  );
  const [selectedAbsentTeacher, setSelectedAbsentTeacher] = useState<
    string | null
  >(null);
  const [selectedAssignedTeacher, setSelectedAssignedTeacher] = useState<
    string | null
  >(null);
  const [selectedLesson, setSelectedLesson] = useState<LessonDetail | null>(
    null
  );
  const [coverTeachers, setCoverTeachers] = useState<SelectedCoverTeacher[]>(
    []
  );
  const [timetableType, setTimetableType] =
    useState<TimetableType>(TIMETABLE_TYPE_DEFAULT);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodDetail | null>(
    null
  );
  const popover1 = useRef<HTMLIonPopoverElement>(null);
  const popover2 = useRef<HTMLIonPopoverElement>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { myRoomList, myRoomListLoaded } = useRoomList();
  const {
    dictionary: dictionaryAbsenceReasons,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    refreshDictionary: refreshAbsenceReasons,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dictionaryLoaded: absenceReasonsLoaded,
  } = useDictionary("AbsenceReasons");

  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    lessonDetail,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    lessonSaving,
    addCoverTeacher,
    removeCoverTeacher,
    updateLesson,
  } = useLessonDetail(selectedLesson?._id);

  const {
    isLoading: teacherAbsencesLoading,
    teacherAbsences,
    refreshTeacherAbsences,
    teacherAbsencesLoaded,
    teacherAbsencesRange,
    refreshAbsenceRange,
    saveAbsence,
    deleteAbsence,
  } = useTeacherAbsences(absenceDate);

  const {
    isLoading: coverTeachersLoading,
    myCoverTeachers: assignedCoverTeachers,
    refreshMyCoverTeachers: refreshAssignedCoverTeachers,
  } = useCoverTeachers(absenceDate, absenceDate, selectedAbsentTeacher);

  const { teacherLessons, refreshTeacherLessons, teacherLessonsLoaded } =
    useTeacherLessons(absenceDate, absenceDate, selectedAbsentTeacher, selectedAssignedTeacher);

  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    register,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleSubmit,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    watch,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { errors },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setValue,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    reset,
  } = useForm<CoverFormInputs>();

  // useEffect(() => {
  //   // Make sure cover form has the correct values from selected lesson
  //   setValue("cover_summary", selectedLesson?.CoverWork?.Summary || "");
  //   setValue("cover_detail", selectedLesson?.CoverWork?.Detail || "");
  // }, [selectedLesson]);

  useEffect(() => {
    if (absenceDate && selectedAbsentTeacher) {
      setSelectedAbsentTeacher(null);
      setSelectedAssignedTeacher(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [absenceDate]);

  // const handleSwapRoomChange = (e: any) => {
  //   setValue("room", e.detail.value);
  // };

  // MUI Select Events
  const handleCoverTeacherSelect = async (teacher: any, inst?: any) => {
    console.log("handleCoverTeacherSelect", teacher, inst);

    const teacherData = inst?.props?.data?.find(
      (d: any) => d?.value === teacher?.value
    );
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const t = {
      _id: teacher.value,
      Name: teacher?.valueText,
      data: teacherData,
    };

    if (teacher?.value) {
      const tt = { _id: teacher?.value, Name: teacher?.valueText, Email: teacherData?.Email };
      await handleAddCoverTeacher(tt);
    }
  };

  const handleAddCoverTeacher = async (teacher: any) => {
    console.log("handleAddCoverTeacher", teacher);
    if (teacher) {
      const exists = coverTeachers.find((t) => t._id === teacher._id);
      if (exists) {
        displayMsg(`${teacher?.Name} is already covering this lesson`, {
          type: "warning",
          position: "middle",
        });
        return;
      }

      const newTeacher: SelectedCoverTeacher = {
        _id: teacher._id,
        Name: teacher?.Name,
        Email: teacher?.Email,
      };
      await addCoverTeacher(newTeacher._id);
      setCoverTeachers([...coverTeachers, newTeacher]);
      refreshTeacherLessons();
    }
  };

  const handleDeleteCoverTeacher = async (teacher: any) => {
    await removeCoverTeacher(teacher._id);
    setCoverTeachers(
      coverTeachers.filter((t: SelectedCoverTeacher) => t._id !== teacher._id)
    );
    refreshTeacherLessons();
  };

  const handleAbsenceDateChange = (d: any) => {
    const dateString = formatDate(d);
    setAbsenceDate(dateString);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    setSelectedAbsenceDate && setSelectedAbsenceDate(dateString);
    refreshTeacherAbsences();
    refreshTeacherLessons();
    refreshAssignedCoverTeachers();
    refreshAbsenceRange(firstDate, lastDate);
    // Reset selected teacher, period, lesson and timetable type
    setSelectedLesson(null);
    setSelectedPeriod(null);
    setSelectedAssignedTeacher(null);
    setCoverTeachers([]);
    setTimetableType(TIMETABLE_TYPE_DEFAULT);
    // Reset the form
    // reset();
  };

  const handleAbsenceClick = (absence: any) => {
    console.log(absence);
    // Deselect selected assigned teacher
    setSelectedAssignedTeacher(null);
    if (absence?.TeacherId === selectedAbsentTeacher) {
      setSelectedAbsentTeacher(null);
    } else {
      setSelectedAbsentTeacher(absence?.TeacherId);
    }
    setTimeout(refreshTeacherLessons, 200);
  };

  const handleAssignedCoverClick = (coverTeacher: any) => {
    console.log(coverTeacher);
    // Deselect selected absent teacher
    setSelectedAbsentTeacher(null);
    if (coverTeacher?._id === selectedAssignedTeacher) {
      setSelectedAssignedTeacher(null);
    } else {
      setSelectedAssignedTeacher(coverTeacher?._id);
    }
    setTimeout(refreshTeacherLessons, 200);
  };

  const handleLessonClick = (lesson: LessonDetail) => {
    console.log(lesson);

    // Show informational message for lessons that don't require cover, but always allow selection
    if (!doesLessonRequireCover(lesson)) {
      displayMsg("This lesson currently does not require cover teachers", {
        type: "warning",
        duration: 3000,
      });
    }

    if (lesson?._id === selectedLesson?._id) {
      setSelectedLesson(null);
      setCoverTeachers([]);
    } else {
      setSelectedLesson(lesson);
      setCoverTeachers(lesson?.CoverTeachers || []);
    }
    // reset();
  };

  const handleClearAbsentTeacher = () => {
    setSelectedAbsentTeacher(null);
    setSelectedAssignedTeacher(null);
    setSelectedLesson(null);
  };

  const handleClearAssignedTeacher = () => {
    setSelectedAssignedTeacher(null);
    setSelectedAbsentTeacher(null);
    setSelectedLesson(null);
  }

  const handleClearSelectedPeriod = () => {
    setSelectedPeriod(null);
    setSelectedLesson(null);
  }

  /* Calendar bits */
  /**
   * Render a calendar day with a number if there are absences on that day.
   * Also indicator icons are shown if there are lessons that require cover teachers
   * or if there are lessons that require cover work.
   * @param args
   * @returns
   */
  const customDay = useCallback((args: any) => {
    const { date } = args;
    let absTotal: number = 0;
    let coversRequired: number = 0;
    let workRequired: number = 0;

    if (teacherAbsencesRange.length > 0) {
      const todays = teacherAbsencesRange.filter((abs: any) => {
        // return try if date falls between abs.StartAt and abs.EndAt and not a saturday or sunday
        // Convert date to current timezone
        const dateOnly = dateAndTime.format(
          new Date(date.toISOString()),
          "YYYY-MM-DD"
        );
        // Get date only from abs.StartAt and abs.EndAt
        const absStartAt = abs?.StartAt.split("T")[0];
        const absEndAt = abs?.EndAt.split("T")[0];
        return (
          dateOnly >= absStartAt &&
          dateOnly <= absEndAt &&
          date.getDay() !== 0 &&
          date.getDay() !== 6
        );
      });
      absTotal = todays.length;
      coversRequired = todays.reduce((acc: number, curr: any) => {
        return acc + curr?.LessonCountNoCoverTeachers;
      }, 0);
      workRequired = todays.reduce((acc: number, curr: any) => {
        return acc + curr?.LessonCountNoCoverWork;
      }, 0);
    }

    return (
      absTotal > 0 && (
        <div
          className={
            "calendar-picker-label " +
            (absTotal > 0 ? "teacher-absences " : "") +
            (coversRequired > 0 ? "cover-teachers-required " : "") +
            (workRequired > 0 ? "cover-work-required " : "")
          }
        >
          <div className="day-line"></div>
          <div className="day-content">
            <div className="day-total">{absTotal ? absTotal : ""}</div>
            <PersonIcon className="person-icon indicator-icon" />
            <WorkIcon className="work-icon indicator-icon" />
          </div>
        </div>
      )
    );
  }, [teacherAbsencesRange]);

  const handleCellClick = (event: any, inst: any) => {
    console.log("Cell clicked", event, inst);
    const { date, disabled } = event;
    if (!disabled) {
      handleAbsenceDateChange(date);
    }
  };

  const handleRefreshClick = () => {
    refreshTeacherAbsences();
    refreshTeacherLessons();
    refreshAssignedCoverTeachers();
    refreshAbsenceRange(firstDate, lastDate);
  }

  const reloadDatePicker = () => {
    console.log("reloadDatePicker");
    refreshAbsenceRange(firstDate, lastDate);
  }

  const handlePageLoaded = useCallback((event: any, _inst: any) => {
    // console.log("Page loaded", event, inst);
    // console.log("first day", event.firstDay);
    // console.log("last day", event.lastDay);
    // console.log("view start", event.viewStart);
    // console.log("view end", event.viewEnd);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    setFirstDate && setFirstDate(event.firstDay);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    setLastDate && setLastDate(event.lastDay);
    refreshAbsenceRange(event.firstDay, event.lastDay);
  }, [setFirstDate, setLastDate, refreshAbsenceRange]);

  const handleTimetableTypeChange = async (type: TimetableType) => {
    console.log("handleTimetableTypeChange", type);
    setTimetableType(type);
  };

  const handlePeriodSelected = async (period: PeriodDetail) => {
    console.log("handlePeriodSelected", period);
    if (period.Period === selectedPeriod?.Period) {
      setSelectedPeriod(null);
    } else {
      setSelectedPeriod(period);
    }
  };

  const handleLessonUpdated = async (lesson: LessonDetail) => {
    console.log("handleLessonUpdated", lesson);
    // Find lesson in teacherLessons state and update the state
    const index = teacherLessons.findIndex((l: LessonDetail) => l._id === lesson._id);
    if (index > -1) {
      teacherLessons[index] = lesson;
    }

    setTimeout(refreshTeacherLessons, 100);
    setSelectedLesson(lesson);
  };

  const handleLessonDeleted = async (lesson: LessonDetail) => {
    console.log("handleLessonDeleted", lesson);
    setSelectedLesson(null);
    setTimeout(handleRefreshClick, 1000);
  }

  const handleOnCallSelected = async (teacher: OnCallTeacher) => {
    console.log("handleOnCallSelected", teacher);
    await handleAddCoverTeacher({ _id: teacher.id, Name: teacher.Name, Email: teacher?.Email });
  };

  const handleAbsentTeacherSelect = async (e: any, inst?: any) => {
    console.log("handleAbsentTeacherSelect", e);
    if (e?.value) {
      const teacherData = inst?.props?.data?.find(
        (d: any) => d?.value === e?.value
      );
      const teacher = {
        _id: e.value,
        Name: e?.valueText,
        data: teacherData,
      };
      await handleAddAbsentTeacher(teacher);
    }
  };

  const handleAddAbsentTeacher = async (teacher: any) => {
    console.log("handleAddAbsentTeacher", teacher);
    if (teacher) {
      const exists = teacherAbsences.find(
        (t: any) => t.TeacherId === teacher._id
      );
      if (exists) {
        displayMsg(`${teacher?.Name} is already absent today`, {
          type: "warning",
          position: "middle",
        });
        return;
      }

      const newAbsence: TeacherAbsence = {
        Reason: "Entered by Covers Admin",
        StartAt: absenceDate || "",
        EndAt: absenceDate || "",
        ContactId: teacher?.data?.teacher?.ForeignKeys?.Edumate?.ContactId,
      };

      await saveAbsence(newAbsence);
      setTimeout(refreshTeacherAbsences, 2000);
      setTimeout(refreshTeacherLessons, 3000);
      setTimeout(reloadDatePicker, 2000);
      setSelectedAbsentTeacher(null);
    }
  };

  const handleDeleteTeacherAbsence = async (absence: any) => {
    console.log("handleDeleteTeacherAbsence", absence);
    const absenceId = absence?._id;

    if (absenceId) {
      await deleteAbsence(absenceId);
      setTimeout(refreshTeacherLessons, 2000);
      setTimeout(reloadDatePicker, 2000);
      setSelectedAbsentTeacher(null);
      displayMsg("Teacher absence deleted", { type: "success", position: "middle" });
    }
    else {
      console.log("absenceId is null");
      displayMsg("Error: absenceId is null", { type: "error", position: "middle" });
    }
  }

  const calendarHeader = () => {
    return <Fragment>
      <CalendarNav />
      <div className="custom-buttons">
        <CalendarPrev />
        <CalendarToday />
        <CalendarNext />
      </div>
    </Fragment>;
  }

  return (
    <PymblePage cssClass="home-page" hideFiller={true}>
      <IonGrid>
        <IonRow>
          <IonCol
            size="12"
            size-md="6"
            size-lg="3"
            size-xl="3"
            className="col1"
          >
            {/* Search */}
            <IonGrid>
              <IonRow>
                <IonCol>
                  <IonItem lines="none">
                    <IonText>
                      <h3>Cover Date</h3>
                    </IonText>
                    <IonButton slot="end" fill="clear" title="Refresh" onClick={handleRefreshClick}>
                      <IonIcon icon={refresh} />
                    </IonButton>
                  </IonItem>
                  <Datepicker
                    // key={"datepicker-" + teacherAbsencesRange.length}
                    controls={["calendar"]}
                    display="anchored"
                    animation="slide-up"
                    // calendarSize={2}
                    // calendarType="week"
                    touchUi={true}
                    value={absenceDate}
                    dateFormat="DDD, DD MMM YYYY"
                    renderDayContent={customDay}
                    renderCalendarHeader={calendarHeader}
                    onPageLoaded={handlePageLoaded}
                    onCellClick={handleCellClick}
                    theme="material"
                    cssClass="covers-calendar-picker"
                    labelStyle="floating"
                  />
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol>
                  <IonItem >
                    <IonText>
                      <h3>Absent Teachers</h3>
                    </IonText>

                    <IonButton id="add-new-absence" fill="clear" slot="end"><IonIcon icon={addCircle} slot="icon-only" color="danger" /></IonButton>
                    {teacherAbsencesLoading && <IonSpinner name="crescent" color="primary" />}
                    {selectedAbsentTeacher && (
                      <IonButton
                        onClick={handleClearAbsentTeacher}
                        slot="end"
                        fill="clear"
                        className="ion-text-capitalize"
                      >
                        Clear Selection
                      </IonButton>
                    )}
                  </IonItem>
                  <IonPopover ref={popover1} trigger="add-new-absence" triggerAction="click">
                    <IonContent class="ion-padding">
                      <IonItem lines="none">
                        {"Search"}
                        <ClosePopoverButton popover={popover1} />
                      </IonItem>
                      <CoverTeacherSelect
                        onChange={handleAbsentTeacherSelect}
                        searchDate={absenceDate ? formatDate(absenceDate) : ""}
                        placeholder="Add absent teacher..."
                        display="inline"
                      // isOpen={true}
                      />

                    </IonContent>
                  </IonPopover>
                  <IonList className="absent-teacher-list">
                    {!teacherAbsencesLoaded && <Spinner />}
                    {teacherAbsences &&
                      [...teacherAbsences] // Create a shallow copy to avoid mutating the original array.
                        .sort((a, b) => a.FamilyName.toLowerCase().localeCompare(b.FamilyName.toLowerCase())) // Sort the array by Name, case-insensitive.
                        .map((absence: any) => {
                          const dictionaryReason = dictionaryAbsenceReasons?.find((r: any) => r?._id === absence?.Type);
                          const reason: string = dictionaryReason ? dictionaryReason?.Value : absence?.Reason ?? "Unknown Reason";

                          return (
                            <Fragment key={absence._id}>
                              <IonItem
                                onClick={() => handleAbsenceClick(absence)}
                                className={
                                  "absence-teacher " +
                                  (absence?.TeacherId === selectedAbsentTeacher
                                    ? "absence-teacher-selected"
                                    : "")
                                }
                                title={absence?.Reason + "  " + formatDateTime(absence?.StartAt, true) + " to " + formatDateTime(absence?.EndAt, true)}
                              >
                                <div slot="start">
                                  <div className={"teacher-profile-wrapper profile-absent"}>
                                    <ContactProfileImage
                                      cssClass={"list-profile-image profile-absent"}
                                      profile={{ contactId: absence?.TeacherId }}
                                      onSelect={() => {
                                        console.log("disabled.");
                                      }}
                                    />
                                  </div>
                                </div>
                                <IonLabel>
                                  {absence.Name}
                                  {absence?.Source ? <HtmlText htmltext={`<span class="teacher-absence-source"> - ( ${absence?.Source} )</span>`} /> : ""}
                                  {reason ? <HtmlText htmltext={`<span class="teacher-absence-reason"><br>${reason}</span>`} /> : ""}
                                </IonLabel>
                                {
                                  // If absence.StartAt is 00:00 and absence.EndtAt is 23:59 then don't show the time
                                  formatDateTime(absence?.StartAt, true).split(" ")[1] === "00:00" && formatDateTime(absence?.EndAt, true).split(" ")[1] === "23:59" ? "All Day" :
                                    <span className="absent-teacher-extra">
                                      <TimeShort time={formatDateTime(absence?.StartAt, true)} /><HtmlText htmltext="&nbsp;-&nbsp;" /><TimeShort time={formatDateTime(absence?.EndAt, true)} />
                                    </span>
                                }
                                <IonIcon icon={trashOutline} slot="end" title="Delete Teacher Absence" onClick={() => handleDeleteTeacherAbsence(absence)} />
                              </IonItem>
                            </Fragment>
                          );
                        })}
                    {selectedAbsentTeacher && (
                      <IonItem lines="none">
                        <IonButton
                          onClick={handleClearAbsentTeacher}
                          slot="end"
                        >
                          Clear Selection
                        </IonButton>
                      </IonItem>
                    )}
                  </IonList>
                </IonCol>
              </IonRow>



              <IonRow>
                <IonCol>
                  <IonItem >
                    <IonText>
                      <h3>Assigned Cover Teachers</h3>
                    </IonText>

                    {coverTeachersLoading && <IonSpinner name="crescent" color="primary" />}
                    {selectedAssignedTeacher && (
                      <IonButton
                        onClick={handleClearAssignedTeacher}
                        slot="end"
                        fill="clear"
                        className="ion-text-capitalize"
                      >
                        Clear Selection
                      </IonButton>
                    )}
                  </IonItem>
                  <IonList className="asigned-cover-teacher-list">
                    {coverTeachersLoading && <Spinner />}
                    {assignedCoverTeachers.length >= 0 &&
                      assignedCoverTeachers // Create a shallow copy to avoid mutating the original array.
                        .map((ct: any) => {
                          return (
                            <Fragment key={ct._id}>
                              <IonItem
                                onClick={() => handleAssignedCoverClick(ct)}
                                className={
                                  "assigned-cover-teacher " +
                                  (ct?._id === selectedAssignedTeacher
                                    ? "assigned-cover-teacher-selected"
                                    : "")
                                }
                              >
                                <div slot="start">
                                  <ContactProfileImage
                                    cssClass={"list-profile-image"}
                                    profile={{ contactId: ct?._id }}
                                    onSelect={() => {
                                      console.log("disabled.");
                                    }}
                                  />
                                </div>
                                <div slot="end">
                                  <IonButton href={"mailto:" + ct?.email} shape="round" fill="clear">
                                    <IonIcon slot="icon-only" icon={mailOutline} />
                                  </IonButton>
                                </div>
                                <IonLabel>{ct?.Name}
                                  <p>
                                    <IonText className={ct?.CoverCount > 4 ? "assigned-cover-danger" :
                                      (ct?.CoverCount > 2 ? "assigned-cover-warn" : "assigned-cover-ok")}>
                                      {ct?.CoverCount} {ct?.CoverCount > 1 ? "periods" : "period"}
                                    </IonText>
                                  </p>
                                </IonLabel>

                              </IonItem>
                            </Fragment>
                          );
                        })}
                  </IonList>
                </IonCol>
              </IonRow>


            </IonGrid>
          </IonCol>
          <IonCol
            size="12"
            size-md="6"
            size-lg="4"
            size-xl="4"
            className="col2"
          >
            <IonGrid className="lessons-wrapper">
              <IonRow>
                <IonCol>
                  {/* Lessons */}
                  {teacherLessons.length === 0 && (
                    <IonItem lines="none">
                      <IonText>
                        <h3>No lessons to cover {selectedAbsentTeacher ? " for selected teacher" : ""}</h3>
                      </IonText>
                    </IonItem>
                  )}
                  {teacherLessons.length > 0 && (
                    <Fragment>
                      <IonItem lines="none">
                        <h3>Lessons to Cover</h3>
                        {(selectedPeriod || selectedLesson) && (
                          <IonButton
                            onClick={handleClearSelectedPeriod}
                            slot="end"
                            fill="clear"
                            className="ion-text-capitalize"
                          >
                            Clear Selection
                          </IonButton>
                        )}
                        <AddLessonButton
                          timetableType={timetableType}
                          timetableDate={absenceDate}
                          timetablePeriod={selectedPeriod?.PeriodId}
                          onAddLesson={handleRefreshClick}  // Reload after adding a lesson
                          onAddPeriods={handleRefreshClick} // Reload after adding periods
                        />
                      </IonItem>
                      <TimetableSelector
                        timetableType={timetableType}
                        onSelected={handleTimetableTypeChange}
                      />

                      <PeriodSelector
                        key={absenceDate + timetableType}
                        calendarDate={absenceDate ?? ""}
                        timetableType={timetableType}
                        lessons={teacherLessons}
                        onSelected={handlePeriodSelected}
                        value={selectedPeriod}
                      />
                    </Fragment>
                  )}
                  <IonList className="cover-lesson-list" key={selectedAbsenceDate}>
                    {!teacherLessonsLoaded && <Spinner />}
                    {teacherLessons &&
                      teacherLessons.map((lesson: any) => {
                        const workAssigned =
                          (lesson?.CoverWork?.Summary ?? "") !== "";
                        const teacherAssigned = lesson?.CoverTeachers?.length > 0;
                        let showLesson = true;
                        if (selectedPeriod) {
                          showLesson =
                            lesson?.Class?.PeriodId === selectedPeriod?.PeriodId;
                        } else {
                          showLesson =
                            lesson?.Class?.TimetableType === timetableType ||
                            timetableType === "All";
                        }
                        // Don't show lesson if it is cancelled
                        if (lesson?.Cancelled) {
                          showLesson = false;
                        }
                        return (
                          showLesson && (
                            <Fragment key={lesson._id}>
                              <IonItem
                                onClick={() => handleLessonClick(lesson)}
                                className={
                                  (!doesLessonRequireCover(lesson)
                                    ? "lesson-no-cover-required"
                                    : "") +
                                  (lesson?._id === selectedLesson?._id
                                    ? " lesson-selected"
                                    : "")
                                }
                              >
                                <IonText slot="start">
                                  <TimeShort time={lesson?.LessonStartTime} />
                                </IonText>
                                <IonLabel>
                                  {lesson?.Title ? lesson?.Title : lesson?.Class?.Title ? lesson?.Class?.Title : lesson?.Type}
                                  {lesson?.Class?.NameShort ? <HtmlText htmltext={`<span class="lesson-class-sub"> ( ${lesson?.Class?.NameShort} )</span></br>`} /> : <HtmlText htmltext="</br>" />}
                                  {lesson?.Class?.TeacherCode ? <HtmlText htmltext={`<span class="lesson-class-sub  ${lesson?.Absence?._id != null ? "teacher-absent" : ""}">${lesson?.Class?.TeacherCode}</span>`} /> : ""}
                                  {lesson?.Type === "Event" ? <HtmlText htmltext={`<span class="lesson-class-sub"> [ EVENT ]</span>`} /> : ""}
                                  {lesson?.Absence?._id == null && lesson?.Type !== "Event" ? <HtmlText htmltext={`<span class="lesson-teacher-swap-name"> - [ SWAP: ${lesson?.Absence?.Teacher?.Name} ]</span>`} /> : ""}
                                  {lesson?.CoverTeachers?.length > 0 ? <HtmlText htmltext={`<span class="lesson-class-sub teacher-cover"> > [ COVER: ${lesson?.CoverTeachers[0]?.Name} ]</span>`} /> : ""}
                                </IonLabel>
                                <IonIcon
                                  slot="end"
                                  icon={teacherAssigned ? personOutline : person}
                                  color={
                                    !doesLessonRequireCover(lesson)
                                      ? "medium"
                                      : teacherAssigned
                                        ? "success"
                                        : "danger"
                                  }
                                  title={
                                    teacherAssigned ? "Cover Assigned" : "Needs Cover"
                                  }
                                />
                                <IonIcon
                                  slot="end"
                                  icon={workAssigned ? fileTrayFullOutline : fileTray}
                                  color={
                                    !doesLessonRequireCover(lesson)
                                      ? "medium"
                                      : workAssigned
                                        ? "success"
                                        : "danger"
                                  }
                                  title={
                                    workAssigned ? "Work Assigned" : "Needs Work"
                                  }
                                />
                              </IonItem>
                            </Fragment>
                          )
                        );
                      })}
                  </IonList>


                </IonCol>
              </IonRow>
            </IonGrid>
          </IonCol>
          <IonCol
            size="12"
            size-md="6"
            size-lg="5"
            size-xl="5"
            className="col13"
          >
            <IonGrid className="class-details-wrapper">
              <IonRow>
                <IonCol>
                  {/* Cover Teachers */}
                  {selectedLesson && (
                    <Fragment>
                      <IonItem lines="none">
                        <IonText><h3>{"Class Details" + (selectedLesson?.Type === "Event" ? " (Event)" : "")}</h3></IonText>
                      </IonItem>
                      {!app.notifyCoverTeachers && (
                        <IonItem className="no-notify" color="tertiary">
                          <IonLabel color="primary">
                            NOTE: Notifications are disabled in config
                          </IonLabel>
                        </IonItem>
                      )}

                      <IonGrid className="class-details-grid">
                        <IonRow>
                          <IonCol>
                            <IonItem lines="none">
                              <IonText >
                                <h3>{selectedLesson?.Title + (selectedLesson?.Type === "Event" ? " (Event)" : "")}</h3>
                              </IonText>
                              <IonIcon icon={calendarOutline} color="primary" size="small" slot="end" />
                              <IonText slot="end">
                                Period
                                {" " + selectedLesson?.Class?.PeriodName}
                              </IonText>
                              <div slot="end">
                                <TimeShort time={selectedLesson?.LessonStartTime} />
                              </div>
                            </IonItem>
                            {selectedLesson?.Class?.Teacher?._id && (

                              <IonItem className="absent-teacher-item">
                                <div slot="start">
                                  <div className={"teacher-profile-wrapper profile-absent"}>
                                    <ContactProfileImage
                                      cssClass={"list-profile-image profile-absent"}
                                      profile={{ contactId: selectedLesson?.Class?.Teacher?._id ?? "" }}
                                      onSelect={() => {
                                        console.log("disabled.");
                                      }}
                                    />
                                  </div>
                                </div>
                                <IonLabel>
                                  {selectedLesson?.Class?.Teacher?.Name}
                                  <p>
                                    Regular Teacher
                                  </p>
                                </IonLabel>
                                <div slot="end">
                                  <IonButton href={"mailto:" + selectedLesson?.Class?.Teacher?._id} shape="round" fill="clear">
                                    <IonIcon slot="icon-only" icon={mailOutline} />
                                  </IonButton>
                                </div>
                              </IonItem>)
                            }

                            <IonItem lines="none">
                              <IonCheckbox
                                slot="start"
                                checked={selectedLesson?.RequiresCover ?? Boolean(selectedLesson?.Class?.CoverTeachersRequired)}
                                onIonChange={async (e) => {
                                  const newValue = e.detail.checked;
                                  if (!newValue && coverTeachers.length > 0) {
                                    // Ask user if they want to remove cover teachers
                                    const shouldRemove = window.confirm(
                                      "This lesson has cover teachers assigned. Do you want to remove them when setting this lesson to not require cover?"
                                    );
                                    if (shouldRemove) {
                                      // Remove all cover teachers
                                      for (const teacher of coverTeachers) {
                                        await removeCoverTeacher(teacher._id);
                                      }
                                      setCoverTeachers([]);
                                    }
                                  }
                                  // Update the lesson with the new RequiresCover value
                                  const updatedLesson = { ...selectedLesson, RequiresCover: newValue };
                                  await updateLesson(updatedLesson);
                                  setSelectedLesson(updatedLesson);
                                  setTimeout(() => refreshTeacherLessons(), 500); // Small delay to ensure server update
                                }}
                              />
                              <IonLabel>
                                <h3>Requires Cover Teacher</h3>
                                <p>
                                  {selectedLesson?.RequiresCover !== null && selectedLesson?.RequiresCover !== undefined
                                    ? "Custom setting for this lesson"
                                    : selectedLesson?.Type === "Event"
                                      ? "Events default to not requiring cover unless explicitly enabled"
                                      // eslint-disable-next-line no-extra-boolean-cast
                                      : `Using class default: ${Boolean(selectedLesson?.Class?.CoverTeachersRequired) ? "requires" : "does not require"} cover`
                                  }
                                </p>
                              </IonLabel>
                              {selectedLesson?.RequiresCover !== null && selectedLesson?.RequiresCover !== undefined && !selectedLesson?.RequiresCover && (
                                <IonButton
                                  slot="end"
                                  fill="clear"
                                  size="small"
                                  onClick={async () => {
                                    // Reset to use class default
                                    const updatedLesson = { ...selectedLesson, RequiresCover: Boolean(selectedLesson?.Class?.CoverTeachersRequired) };
                                    await updateLesson(updatedLesson);
                                    setSelectedLesson(updatedLesson);
                                    setTimeout(() => refreshTeacherLessons(), 500); // Small delay to ensure server update
                                  }}
                                >
                                  Reset to Default
                                </IonButton>
                              )}
                            </IonItem>
                          </IonCol>
                        </IonRow>
                        <IonRow>
                          <IonCol>
                            <IonItem lines="none">
                              <IonText className="class-cover-teacher-title">
                                <h3>Cover Teacher <HtmlText htmltext={coverTeachers.length > 0 ? `<span>${coverTeachers.length} selected</span>` : ""} /></h3>
                              </IonText>
                              <IonButton id="add-new-cover" fill="clear" slot="end">
                                <IonIcon icon={addCircle} slot="icon-only" color="success" />
                              </IonButton>
                            </IonItem>
                            <IonPopover ref={popover2} trigger="add-new-cover" triggerAction="click" className="add-cover-popover">
                              <IonContent class="ion-padding">
                                <IonItem lines="none">
                                  <ClosePopoverButton popover={popover2} />
                                </IonItem>
                                <CoverTeacherChooser
                                  timetableDate={
                                    absenceDate ? formatDate(absenceDate) : ""
                                  }
                                  timetableType={
                                    selectedLesson?.Class?.TimetableType ?? "Secondary"
                                  }
                                  timetablePeriod={
                                    selectedLesson?.Class?.PeriodNameShort ?? ""
                                  }
                                  coverTeachers={coverTeachers}
                                  onSelectOnCallTeacher={handleOnCallSelected}
                                  onSelectAvailableTeacher={handleCoverTeacherSelect}
                                />
                              </IonContent>
                            </IonPopover>
                            <IonGrid>
                              <IonRow>

                                {coverTeachers.length === 0 && (
                                  <IonCol>
                                    <IonItem>
                                      <IonLabel>No cover teachers assigned</IonLabel>
                                    </IonItem>
                                  </IonCol>
                                )}
                                {coverTeachers.map((teacher: SelectedCoverTeacher) => (
                                  <IonCol key={"ctid" + teacher?._id} size="6">
                                    <IonItem className="cover-teacher-item">
                                      <div className={"teacher-profile-wrapper profile-available"} slot="start">
                                        <ContactProfileImage
                                          cssClass={"list-profile-image"}
                                          profile={{ contactId: teacher?._id ?? "" }}
                                          onSelect={() => {
                                            console.log("disabled.");
                                          }}
                                        />
                                      </div>
                                      <IonText >
                                        {teacher?.Name}
                                      </IonText>
                                      <div slot="end">
                                        <IonButton href={"mailto:" + teacher?.Email} shape="round" fill="clear">
                                          <IonIcon slot="icon-only" icon={mailOutline} />
                                        </IonButton>
                                      </div>
                                      <IonButton
                                        slot="end"
                                        fill="clear"
                                        onClick={() => handleDeleteCoverTeacher(teacher)}
                                      >
                                        <IonIcon icon={closeOutline} color="medium" />
                                      </IonButton>
                                    </IonItem>
                                  </IonCol>
                                ))}

                              </IonRow>
                            </IonGrid>
                          </IonCol>
                        </IonRow>
                        {(selectedLesson) && (
                          <CoverWorkForm
                            lesson={selectedLesson}
                            onLessonUpdated={handleLessonUpdated}
                            onLessonDeleted={handleLessonDeleted}
                          />
                        )}
                      </IonGrid>
                    </Fragment>
                  )}
                </IonCol></IonRow></IonGrid>
          </IonCol>
        </IonRow>
      </IonGrid>

    </PymblePage >
  );
};

export default Home;
